// Shared dialog behavior for every modal in the app: Escape-to-close, focus
// capture + trap, and focus restoration. The modals themselves stay plain
// divs built by their callers; this just wires the keyboard/focus contract
// that makes them behave like real dialogs.

export interface ModalBehaviorOpts {
  /** Invoked for Escape (and backdrop click when closeOnBackdrop). The caller
   * owns removal of the backdrop — this helper only reports intent. */
  onRequestClose?: () => void;
  /** Close when the dimmed area outside the modal is clicked. */
  closeOnBackdrop?: boolean;
}

const FOCUSABLE = 'button:not([disabled]), select, input, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Hold-to-peek: while the button is held, fade the panel almost out so the
 * board and its hint arrows read through. The ranked-move panels describe a
 * position they also cover; on a phone there's no room to dock the list beside
 * the board the way the desktop analysers do, so this is the stand-in for
 * "see both at once".
 *
 * Pointer capture is what makes releasing *anywhere* restore the panel — the
 * button is invisible at 5% opacity, so a finger that drifts off it must still
 * deliver the pointerup. Opacity doesn't affect hit-testing, so the held
 * button keeps receiving events the whole time.
 */
export function installPeek(modal: HTMLElement, btn: HTMLButtonElement): void {
  const setPeeking = (on: boolean): void => {
    modal.classList.toggle("peeking", on);
  };

  btn.addEventListener("pointerdown", (e) => {
    // Suppress the synthetic click/focus-on-tap that would otherwise follow;
    // this control is a hold, not a press.
    e.preventDefault();
    btn.setPointerCapture(e.pointerId);
    setPeeking(true);
  });
  const release = (e: PointerEvent): void => {
    if (btn.hasPointerCapture(e.pointerId)) btn.releasePointerCapture(e.pointerId);
    setPeeking(false);
  };
  btn.addEventListener("pointerup", release);
  btn.addEventListener("pointercancel", release);

  // A button has no hold gesture for keyboard users, so mirror it on
  // keydown/keyup. keydown repeats while held; toggling to the same state is
  // a no-op, so the repeat is harmless.
  btn.addEventListener("keydown", (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      setPeeking(true);
    }
  });
  btn.addEventListener("keyup", (e) => {
    if (e.key === " " || e.key === "Enter") setPeeking(false);
  });
  // Focus loss can't leave the panel stuck invisible.
  btn.addEventListener("blur", () => setPeeking(false));
}

// Stack of open dialogs, innermost last. Escape must only close the topmost —
// modals can legitimately stack (the tutor modal's backdrop is click-through,
// so Settings or New Game can open over it), and closing the tutor modal
// implicitly acks the lesson and releases the next dice roll.
type ModalEntry = { backdrop: HTMLElement; onRequestClose?: () => void };
const modalStack: ModalEntry[] = [];

/**
 * Ask the topmost open dialog to close, exactly as Escape would. Used by the
 * Android back button so it dismisses dialogs instead of quitting the app.
 * Returns true if a dialog was open (i.e. the event was consumed) — including
 * when that dialog is deliberately non-dismissible, since back must not fall
 * through to "exit" while a dialog is on screen.
 */
export function closeTopModal(): boolean {
  const top = modalStack[modalStack.length - 1];
  if (!top) return false;
  top.onRequestClose?.();
  return true;
}

/**
 * Install dialog behavior on a `.modal-backdrop` element. Returns a teardown
 * function; it also runs automatically when the backdrop leaves the DOM, so
 * existing `backdrop.remove()` close paths need no extra bookkeeping.
 */
export function installModalBehavior(backdrop: HTMLElement, opts: ModalBehaviorOpts = {}): () => void {
  const modal = backdrop.querySelector<HTMLElement>(".modal") ?? backdrop;
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  const heading = modal.querySelector("h2");
  if (heading && !heading.id) heading.id = `modal-h-${Math.random().toString(36).slice(2, 8)}`;
  if (heading) modal.setAttribute("aria-labelledby", heading.id);

  const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  const entry: ModalEntry = { backdrop, onRequestClose: opts.onRequestClose };
  modalStack.push(entry);

  const isTopmost = (): boolean => modalStack[modalStack.length - 1] === entry;

  const onKeydown = (ev: KeyboardEvent): void => {
    if (!isTopmost()) return;
    if (ev.key === "Escape") {
      ev.stopPropagation();
      opts.onRequestClose?.();
      return;
    }
    if (ev.key !== "Tab") return;
    const focusables = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (ev.shiftKey && (active === first || !modal.contains(active))) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && (active === last || !modal.contains(active))) {
      ev.preventDefault();
      first.focus();
    }
  };
  document.addEventListener("keydown", onKeydown, true);

  if (opts.closeOnBackdrop) {
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) opts.onRequestClose?.();
    });
  }

  // Move focus into the dialog: the primary action if present, else the first
  // focusable control.
  const initial =
    modal.querySelector<HTMLElement>("button.primary:not([disabled])")
    ?? modal.querySelectorAll<HTMLElement>(FOCUSABLE)[0];
  initial?.focus();

  let done = false;
  const teardown = (): void => {
    if (done) return;
    done = true;
    const i = modalStack.indexOf(entry);
    if (i >= 0) modalStack.splice(i, 1);
    document.removeEventListener("keydown", onKeydown, true);
    observer.disconnect();
    // Restore focus only if it's still inside the (now removed) dialog scope.
    if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
  };
  // Auto-teardown when the backdrop is removed from the document.
  const observer = new MutationObserver(() => {
    if (!document.contains(backdrop)) teardown();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return teardown;
}
